<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة الشروط والأحكام</h1>
        <p class="mt-2 text-sm text-gray-700">تعديل الشروط والأحكام للفرع</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="addNewTerm"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة شرط جديد
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="mt-8 text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-2 text-gray-600">جاري التحميل...</p>
    </div>

    <!-- Form -->
    <div v-else class="mt-8 bg-white shadow rounded-lg p-6">
      <div class="space-y-4">
        <!-- Terms List -->
        <div v-for="(term, index) in formData.terms" :key="index" class="border border-gray-200 rounded-lg p-4">
          <div class="flex items-start justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">الشرط {{ index + 1 }}</h3>
            <button
              @click="removeTerm(index)"
              class="text-red-600 hover:text-red-800 text-sm font-medium">
              حذف
            </button>
          </div>
          
          <div class="space-y-4">
            <!-- Title -->
            <div>
              <label :for="`term-title-${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                العنوان (مثل: الأول، الثاني، إلخ)
              </label>
              <input
                v-model="term.title"
                :id="`term-title-${index}`"
                type="text"
                placeholder="مثال: الأول"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <!-- Description -->
            <div>
              <label :for="`term-description-${index}`" class="block text-sm font-medium text-gray-700 mb-2">
                الوصف
              </label>
              <textarea
                v-model="term.description"
                :id="`term-description-${index}`"
                rows="3"
                placeholder="مثال: أن يكون الدارس مُجيداً لتلاوة القرآن الكريم..."
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="formData.terms.length === 0" class="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <p class="text-gray-500">لا توجد شروط وأحكام. اضغط على "إضافة شرط جديد" لإضافة شرط.</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="mt-8 flex justify-end gap-3">
        <button
          type="button"
          @click="handleReset"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          إعادة تعيين
        </button>
        <button
          type="button"
          @click="handleSave"
          :disabled="saving || formData.terms.length === 0"
          class="px-4 py-2 bg-customHafadhColor text-white rounded-md shadow-sm text-sm font-medium hover:bg-customHafadhColor/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
          {{ saving ? 'جاري الحفظ...' : 'حفظ التغييرات' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'TermsAndConditionsManagement',
  data() {
    return {
      loading: false,
      saving: false,
      formData: {
        terms: []
      },
      originalData: {
        terms: []
      },
      alert: {
        show: false,
        type: 'success',
        message: ''
      }
    }
  },
  mounted() {
    this.loadTenantData()
  },
  methods: {
    async loadTenantData() {
      this.loading = true
      try {
        // Get tenant_id from JWT token
        let tenantId = null
        try {
          const token = localStorage.getItem('token')
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            tenantId = payload.tenantId || payload.tenant_id || null
          }
        } catch (e) {
          console.warn('Could not extract tenantId from token:', e)
          this.showAlert('danger', 'فشل في تحديد الفرع')
          this.loading = false
          return
        }

        if (!tenantId) {
          this.showAlert('danger', 'لم يتم العثور على معرف الفرع')
          this.loading = false
          return
        }

        // Fetch tenant data
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.tenants_show + tenantId,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Extract tenant data from response
        let tenant = null
        if (response.data?.result?.data) {
          tenant = response.data.result.data
        } else if (response.data?.data) {
          tenant = response.data.data
        } else if (response.data?.result) {
          tenant = response.data.result
        } else {
          tenant = response.data
        }

        if (tenant) {
          // Parse terms_and_conditions (can be JSON string or array)
          let terms = []
          if (tenant.terms_and_conditions) {
            if (typeof tenant.terms_and_conditions === 'string') {
              try {
                terms = JSON.parse(tenant.terms_and_conditions)
              } catch (e) {
                console.warn('Failed to parse terms_and_conditions:', e)
                terms = []
              }
            } else if (Array.isArray(tenant.terms_and_conditions)) {
              terms = tenant.terms_and_conditions
            }
          }

          // Ensure terms is an array
          if (!Array.isArray(terms) || terms.length === 0) {
            // Default terms
            terms = [
              { title: 'الأول', description: 'أن يكون الدارس مُجيداً لتلاوة القرآن الكريم، متمكنًا من القراءة الصحيحة، مع خلو تلاوته من اللحن الجليِّ' },
              { title: 'الثاني', description: 'الالتزام بالوقت حسب جدول الدارس والجاهزية قبل الموعد ب 10 دقائق' },
              { title: 'الثالث', description: 'التزام الدارس بموعده اليومي' },
              { title: 'الرابع', description: 'أن يكون حافظاً لخمسة أجزاء فأكثر، يتمكن من سردها في مدة لا تتجاوز أسبوعين (أربعة عشر يوماً) كحدٍ أقصى' },
              { title: 'الخامس', description: 'الاستمرار والمواظبة على الخطة طوال العام بما في ذلك أيام الإجازات' }
            ]
          }

          this.formData.terms = terms.map(term => ({
            title: term.title || '',
            description: term.description || ''
          }))
          this.originalData.terms = JSON.parse(JSON.stringify(this.formData.terms))
        }
      } catch (error) {
        console.error('Failed to load tenant data', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تحميل بيانات الفرع')
      } finally {
        this.loading = false
      }
    },
    addNewTerm() {
      this.formData.terms.push({
        title: '',
        description: ''
      })
    },
    removeTerm(index) {
      if (this.formData.terms.length > 1) {
        this.formData.terms.splice(index, 1)
      } else {
        this.showAlert('warning', 'يجب أن يكون هناك شرط واحد على الأقل')
      }
    },
    async handleSave() {
      // Validate
      for (let i = 0; i < this.formData.terms.length; i++) {
        const term = this.formData.terms[i]
        if (!term.title || !term.description) {
          this.showAlert('danger', `يرجى ملء جميع الحقول للشرط ${i + 1}`)
          return
        }
      }

      this.saving = true
      try {
        // Get tenant_id from JWT token
        let tenantId = null
        try {
          const token = localStorage.getItem('token')
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            tenantId = payload.tenantId || payload.tenant_id || null
          }
        } catch (e) {
          console.warn('Could not extract tenantId from token:', e)
          this.showAlert('danger', 'فشل في تحديد الفرع')
          this.saving = false
          return
        }

        if (!tenantId) {
          this.showAlert('danger', 'لم يتم العثور على معرف الفرع')
          this.saving = false
          return
        }

        // Use FormData to send data
        const formData = new FormData()
        formData.append('terms_and_conditions', JSON.stringify(this.formData.terms))
        formData.append('_method', 'PUT')

        console.log('TermsAndConditionsManagement: Sending data', {
          tenantId,
          termsCount: this.formData.terms.length,
          terms: this.formData.terms
        })

        // Update tenant
        const response = await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.tenants_update + tenantId,
          formData,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            },
            transformRequest: [(data) => {
              return data
            }]
          }
        )

        console.log('TermsAndConditionsManagement: Save response', {
          status: response.status,
          data: response.data
        })

        // Reload data to get updated values
        await this.loadTenantData()

        this.originalData.terms = JSON.parse(JSON.stringify(this.formData.terms))
        this.showAlert('success', 'تم حفظ التغييرات بنجاح')
      } catch (error) {
        console.error('Failed to save tenant data', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في حفظ التغييرات')
      } finally {
        this.saving = false
      }
    },
    handleReset() {
      this.formData.terms = JSON.parse(JSON.stringify(this.originalData.terms))
      this.showAlert('info', 'تم إعادة تعيين النموذج')
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    }
  }
})
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors;
}
</style>

